/**
 * AutismPatientGoalsDisplay — read-only view of the person's PREVIOUSLY STATED
 * assessment goals, shown on the Adult Autism PAD "Assessment Outcome" page.
 *
 * This is a faithful clone of the ADHD PAD "Assessment goals" read-only block
 * (1_REV.js): a fixed-width label column ("Assessment goals" + a "(from Patient
 * Self-Report)" / "(No previous answer …)" caption) next to a fixed-width,
 * greyed, non-editable answer box, with a magnifying-glass "source" dialog to
 * inspect the underlying form response.
 *
 * WHAT IT SHOWS
 * Early in the assessment the person answers "Your Goals" in the "Your Current
 * Concerns" intake form (service-user var `assessmentGoals`). That answer is
 * formatted onto the History stage as a prefill. Surfacing it here lets the
 * clinician reference the person's original goals while capturing the goals
 * discussed during the feedback session.
 *
 * IMPORTANT: this is the person's *original* intake goals — distinct from the
 * clinician-captured PAD goals (assessmentOutcome.patientGoalsNotes). It never
 * edits anything.
 *
 * DATA SOURCING (modular pathway)
 * The autism app uses ModularPatientDataContext, so we read:
 *   • the History stage question whose `providerVarName === 'assessmentGoals'`
 *     (its `patientPrefill` is the formatted display string), and
 *   • the raw `formInstances` (for FormattedAnswer italic-quoting and the
 *     magnifying-glass dialog).
 * Both are resolved by the shared History prefill helpers so this stays in
 * lock-step with how the History review page renders the same line item.
 */

import React, { useMemo } from 'react';
import { FormattedAnswer } from '../../utils/formDisplayUtils';
import { resolveHistoryPrefillDisplay } from '../../pages/providers/6_clinicalInterview/autism/resolveHistoryPrefill';
import HistorySourceDataDialog from '../../pages/providers/6_clinicalInterview/autism/HistorySourceDataDialog';

/** The service-user variable that carries the person's intake goals. */
const ASSESSMENT_GOALS_VAR = 'assessmentGoals';

/**
 * Walk the History stage's sections → subsections → questions and return the
 * question whose providerVarName is `assessmentGoals`. Returns null if the
 * History stage has not been built yet (e.g. very early in the assessment).
 */
function findAssessmentGoalsQuestion(historyStage) {
  const sections = historyStage?.data?.sections;
  if (!sections) return null;

  for (const section of Object.values(sections)) {
    for (const subsection of Object.values(section?.subsections || {})) {
      for (const question of Object.values(subsection?.subsectionQuestions || {})) {
        if (question?.providerVarName === ASSESSMENT_GOALS_VAR) return question;
      }
    }
  }
  return null;
}

export default function AutismPatientGoalsDisplay({
  historyStage,
  formInstances,
  patientFirstName = '',
  className = '',
}) {
  // Resolve the display string + raw answer once per data change. Memoised so we
  // don't re-walk the history tree / re-format on every render.
  const { displayText, rawAnswer, inputType, lastUpdatedAt } = useMemo(() => {
    const question = findAssessmentGoalsQuestion(historyStage);
    if (!question) {
      return { displayText: null, rawAnswer: null, inputType: null, lastUpdatedAt: null };
    }
    const resolved = resolveHistoryPrefillDisplay(question, formInstances, 'patient', patientFirstName);
    return {
      ...resolved,
      lastUpdatedAt: question.patientPrefillLastUpdatedAt || null,
    };
  }, [historyStage, formInstances, patientFirstName]);

  const hasAnswer = !!displayText;

  return (
    <div className={`mb-6 mt-2 rounded-lg ${className}`}>
      <div className="text-slate-700 text-base flex flex-wrap text-left items-start gap-y-2">
        {/* Label column — fixed width, matching the ADHD goals block. */}
        <div className="w-96 flex items-center gap-2">
          <span className="font-bold">Assessment goals</span>
          {hasAnswer ? (
            <p className="text-sm italic text-gray-600">(from Patient Self-Report)</p>
          ) : (
            <p className="text-sm italic text-gray-400">(No previous answer given by the person)</p>
          )}
        </div>

        {/* Answer column — fixed-width greyed read-only box + source dialog. */}
        <div className="flex items-start">
          <div className="w-96 p-3 border rounded-md bg-gray-100 text-gray-700 cursor-not-allowed whitespace-pre-wrap">
            <FormattedAnswer
              rawAnswer={rawAnswer}
              displayText={displayText || 'No answer given'}
              inputType={inputType}
            />
          </div>

          {/* Magnifying-glass "source" dialog — only when there is an answer to
              trace back to a form response. */}
          {hasAnswer && (
            <HistorySourceDataDialog
              varNames={[ASSESSMENT_GOALS_VAR]}
              formInstances={formInstances}
              dialogTitle="Patient Form Responses"
              lineItemTitle="Assessment goals"
              displayPrefillText={displayText}
              lastUpdatedAt={lastUpdatedAt}
              patientFirstName={patientFirstName}
            />
          )}
        </div>
      </div>
    </div>
  );
}
